import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { getStoreByOwner, updateStore } from '../lib/firebaseService';
import ImageUpload from '../components/ImageUpload';
import LogoCropper from '../components/LogoCropper';
import { uploadToR2 } from '../lib/r2Upload';
import { ImageIcon } from 'lucide-react';

export default function StoreLogo() {
  const { firebaseUser } = useAuth();
  const [store, setStore] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [showCropper, setShowCropper] = useState(false);
  const [tempLogoUrl, setTempLogoUrl] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (firebaseUser) {
      loadStore();
    }
  }, [firebaseUser]);

  const loadStore = async () => {
    try {
      setPageLoading(true);
      const userStore = await getStoreByOwner(firebaseUser.uid);
      if (userStore) {
        setStore(userStore);
        setLogoUrl(userStore.logo_url || '');
      }
    } catch (error) {
      console.error('Mağaza yüklənə bilmədi:', error);
    } finally {
      setPageLoading(false);
    }
  };

  const handleSave = async () => {
    if (!store) {
      toast.error('Əvvəlcə mağaza yaradın');
      return;
    }

    setSaving(true);
    try {
      await updateStore(store.id, { logo_url: logoUrl });
      toast.success('Logo saxlanıldı');
      loadStore();
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Yüklənir...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!store) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-border p-8 text-center">
            <p className="text-muted-foreground mb-4">Əvvəlcə mağaza yaradın</p>
            <Button onClick={() => window.location.href = '/dashboard/store/info'}>
              Mağaza Yarat
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ImageIcon className="w-8 h-8" />
            Mağaza Loqosu
          </h1>
          <p className="text-muted-foreground mt-1">Mağazanızın loqosunu yükləyin</p>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="space-y-6">
            <div>
              <Label>Logo Şəkli</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Logo dairəvi formada kəsiləcək. Tövsiyə olunan: 500x500px və ya daha böyük
              </p>
              <ImageUpload
                currentImage={logoUrl}
                onUploadComplete={(url) => {
                  setTempLogoUrl(url);
                  setShowCropper(true);
                }}
                label="Logo yüklə"
              />
            </div>

            {logoUrl && (
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="font-semibold mb-3">Cari Logo</h3>
                <div className="flex items-center gap-4">
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg" 
                  />
                  <div className="text-sm text-muted-foreground">
                    <p>Bu logo mağazanızın public səhifəsində</p>
                    <p>profil şəkli kimi görünəcək</p>
                  </div>
                </div>
              </div>
            )}

            {!logoUrl && (
              <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Logo yoxdur. Yuxarıdan logo yükləyin.</p>
              </div>
            )}

            {/* Logo Cropper Modal */}
            {showCropper && tempLogoUrl && (
              <LogoCropper
                imageUrl={tempLogoUrl}
                onCropComplete={async (blob) => {
                  try {
                    const file = new File([blob], 'logo.png', { type: 'image/png' });
                    const uploadedUrl = await uploadToR2(file, 'logos');
                    setLogoUrl(uploadedUrl);
                    setShowCropper(false);
                    setTempLogoUrl(null);
                    toast.success('Logo kəsildi və hazırdır');
                  } catch (error) {
                    console.error('Upload error:', error);
                    toast.error('Logo yüklənə bilmədi');
                  }
                }}
                onCancel={() => {
                  setShowCropper(false);
                  setTempLogoUrl(null);
                }}
              />
            )}

            <div className="pt-4">
              <Button 
                onClick={handleSave} 
                className="w-full h-11" 
                disabled={saving || !logoUrl}
              >
                {saving ? 'Saxlanılır...' : 'Logo-nu Yadda Saxla'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Məsləhət:</strong> Loqonuz yuxarı keyfiyyətli və aydın olmalıdır. Dairəvi kəsilmə üçün mərk əzli şəkil seçin.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}
